// MODIFICATION: Added Routes, Route for nested routing and imported PrescriptionDetail
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import PrescriptionDetail from "./PrescriptionDetail";

import { useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { prescriptionAPI, icdAPI } from "../services/api";
import { formatIcdCodes } from "../utils/icdCodes";

import logo from "./assets/logo.png";
import userAvatar from "./assets/user-avatar.png";
import CameraCapture from "./CameraCapture";
import {
  LogOut,
  FileText,
  Download,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Menu,
  Upload,
  Camera,
  X,
  Search,
} from "lucide-react";

// --- All original sub-components are unchanged ---
const SidebarItem = ({ icon: Icon, label, active, onClick }) => (
  <div
    onClick={onClick}
    className={`flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
      active
        ? "bg-blue-600 text-white"
        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
    }`}
  >
    {" "}
    <div className="flex items-center gap-3">
      {" "}
      <Icon className="w-5 h-5" /> <span>{label}</span>{" "}
    </div>{" "}
  </div>
);
const UserSidebarProfile = ({ user, onLogout, onNavigate }) => (
  <div className="p-4 border-t border-gray-200">
    {" "}
    <div className="flex items-center justify-between">
      {" "}
      <div className="flex items-center gap-3">
        {" "}
        <img
          src={userAvatar}
          alt={user.first_name}
          className="w-10 h-10 rounded-full"
        />{" "}
        <div>
          {" "}
          <button
            onClick={() => onNavigate("profile")}
            className="text-sm font-semibold text-gray-900 text-left hover:underline"
          >
            {" "}
            {user.first_name} {user.last_name}{" "}
          </button>{" "}
          <p className="text-xs text-gray-500 capitalize">{user.role}</p>{" "}
        </div>{" "}
      </div>{" "}
      <button
        onClick={onLogout}
        title="Logout"
        className="text-gray-500 hover:text-red-600"
      >
        {" "}
        <LogOut className="w-5 h-5" />{" "}
      </button>{" "}
    </div>{" "}
  </div>
);
const PrescriptionUpload = ({ onUploadSuccess, onCancel }) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [icdCodes, setIcdCodes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  useEffect(() => {
    (async () => {
      try {
        const response = await icdAPI.getAll();
        const options = response.data.map((code) => ({
          value: code.code,
          label: `${code.code} - ${code.description}`,
        }));
        setIcdCodes(options);
      } catch (error) {
        toast.error("Failed to fetch ICD codes");
      }
    })();
  }, []);
  const handleFileSelect = (file) => {
    if (!file) return;
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];
    const maxSize = 10 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please select an image or PDF.");
      return;
    }
    if (file.size > maxSize) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setSelectedFile(file);
  };
  const handlePhotoCapture = (imageSrc) => {
    setShowCamera(false);
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `capture_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        handleFileSelect(file);
      });
  };
  const onSubmit = async (data) => {
    if (!selectedFile)
      return toast.error("Please select a file or capture a photo");
    if (selectedIcdCodes.length === 0)
      return toast.error("Please select at least one ICD code");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("patient_name", data.patient_name);
      formData.append(
        "icd_codes",
        JSON.stringify(selectedIcdCodes.map((c) => c.value))
      );
      formData.append("file", selectedFile);
      await prescriptionAPI.upload(formData);
      toast.success("Prescription uploaded successfully");
      onUploadSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to upload prescription"
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="max-w-lg mx-auto">
      {" "}
      <form onSubmit={handleSubmit(onSubmit)}>
        {" "}
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100 space-y-6">
          {" "}
          <div>
            {" "}
            <label
              htmlFor="patient_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Patient name
            </label>{" "}
            <input
              id="patient_name"
              type="text"
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                errors.patient_name ? "border-red-500" : ""
              }`}
              placeholder="Enter patient's full name"
              {...register("patient_name", {
                required: "Patient name is required",
              })}
            />{" "}
          </div>{" "}
          <div>
            {" "}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ICD Codes <span className="text-gray-400">(At least one)</span>
            </label>{" "}
            <Select
              isMulti
              options={icdCodes}
              value={selectedIcdCodes}
              onChange={(opts) => setSelectedIcdCodes(opts || [])}
              placeholder="Select one or more codes..."
              className="react-select-container"
              classNamePrefix="react-select"
            />{" "}
          </div>{" "}
          <div>
            {" "}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prescription file{" "}
              <span className="text-gray-400">(Image/PDF)</span>
            </label>{" "}
            <div className="mt-2 p-4 border-2 border-dashed border-gray-200 rounded-xl">
              {" "}
              {selectedFile ? (
                <div className="flex items-center justify-between">
                  {" "}
                  <span className="text-sm font-medium text-green-600 truncate pr-2">
                    File: {selectedFile.name}
                  </span>{" "}
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    title="Remove file"
                  >
                    {" "}
                    <X className="w-5 h-5 text-red-500 flex-shrink-0" />{" "}
                  </button>{" "}
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4">
                  {" "}
                  <button
                    type="button"
                    onClick={() => setShowCamera(true)}
                    className="flex-1 flex flex-col items-center justify-center gap-2 p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {" "}
                    <Camera className="w-6 h-6 text-gray-600" />{" "}
                    <span className="text-sm font-semibold text-gray-800">
                      Open Camera
                    </span>{" "}
                  </button>{" "}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 flex flex-col items-center justify-center gap-2 p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {" "}
                    <Upload className="w-6 h-6 text-gray-600" />{" "}
                    <span className="text-sm font-semibold text-gray-800">
                      Choose File
                    </span>{" "}
                  </button>{" "}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />{" "}
                </div>
              )}{" "}
            </div>{" "}
          </div>{" "}
          <div className="flex flex-col sm:flex-row gap-4 pt-2">
            {" "}
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-3 px-4 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>{" "}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {" "}
              {loading && <div className="spinner-white mr-2"></div>}{" "}
              {loading ? "Uploading..." : "Upload"}{" "}
            </button>{" "}
          </div>{" "}
        </div>{" "}
      </form>{" "}
      {showCamera && (
        <CameraCapture
          onCapture={handlePhotoCapture}
          onClose={() => setShowCamera(false)}
        />
      )}{" "}
    </div>
  );
};
const ProfilePage = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    formState: { errors: profileErrors, isSubmitting: isUpdatingProfile },
  } = useForm({
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
    },
  });
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isChangingPassword },
  } = useForm();
  const onUpdateProfile = async (data) => {
    try {
      await updateProfile(data);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update profile.");
    }
  };
  const onChangePassword = async (data) => {
    if (data.new_password.length < 6) {
      return toast.error("New password must be at least 6 characters long.");
    }
    try {
      await changePassword(data);
      toast.success("Password changed successfully!");
      resetPassword();
    } catch (error) {
      toast.error(error.message || "Failed to change password.");
    }
  };
  useEffect(() => {
    if (user) {
      resetProfile({ first_name: user.first_name, last_name: user.last_name });
    }
  }, [user, resetProfile]);
  return (
    <div className="max-w-xl mx-auto space-y-6 md:space-y-8">
      {" "}
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100">
        {" "}
        <form
          onSubmit={handleSubmitProfile(onUpdateProfile)}
          className="space-y-6"
        >
          {" "}
          <div>
            {" "}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>{" "}
            <input
              type="email"
              value={user?.email || "example@example.com"}
              disabled
              className="w-full p-3 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 cursor-not-allowed"
            />{" "}
          </div>{" "}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {" "}
            <div>
              {" "}
              <label
                htmlFor="first_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                First name
              </label>{" "}
              <input
                id="first_name"
                type="text"
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  profileErrors.first_name ? "border-red-500" : ""
                }`}
                {...registerProfile("first_name", {
                  required: "First name is required",
                })}
              />{" "}
            </div>{" "}
            <div>
              {" "}
              <label
                htmlFor="last_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Last name
              </label>{" "}
              <input
                id="last_name"
                type="text"
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  profileErrors.last_name ? "border-red-500" : ""
                }`}
                {...registerProfile("last_name", {
                  required: "Last name is required",
                })}
              />{" "}
            </div>{" "}
          </div>{" "}
          <div className="pt-2">
            {" "}
            <button
              type="submit"
              disabled={isUpdatingProfile}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {" "}
              {isUpdatingProfile ? "Updating..." : "Update"}{" "}
            </button>{" "}
          </div>{" "}
        </form>{" "}
      </div>{" "}
      <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg border border-gray-100">
        {" "}
        <form
          onSubmit={handleSubmitPassword(onChangePassword)}
          className="space-y-6"
        >
          {" "}
          <div>
            {" "}
            <label
              htmlFor="current_password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Current Password
            </label>{" "}
            <input
              id="current_password"
              type="password"
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                passwordErrors.current_password ? "border-red-500" : ""
              }`}
              placeholder="Enter current password"
              {...registerPassword("current_password", {
                required: "Current password is required",
              })}
            />{" "}
          </div>{" "}
          <div>
            {" "}
            <label
              htmlFor="new_password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Password
            </label>{" "}
            <input
              id="new_password"
              type="password"
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                passwordErrors.new_password ? "border-red-500" : ""
              }`}
              placeholder="Enter new password"
              {...registerPassword("new_password", {
                required: "New password is required",
              })}
            />{" "}
          </div>{" "}
          <div className="pt-2">
            {" "}
            <button
              type="submit"
              disabled={isChangingPassword}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {" "}
              {isChangingPassword ? "Changing..." : "Change Password"}{" "}
            </button>{" "}
          </div>{" "}
        </form>{" "}
      </div>{" "}
    </div>
  );
};

// MODIFICATION: The DashboardView component is now defined outside of Dashboard.
// It receives all the state and functions it needs as props to prevent re-renders.
const DashboardView = ({
  activeTab,
  pageTitle,
  setSidebarOpen,
  handleTabClick,
  searchTerm,
  setSearchTerm,
  fetchPrescriptions,
  paginatedPrescriptions,
  loading,
  paginationProps,
}) => (
  <>
    <header className="flex items-center justify-between mb-6 md:mb-8">
      <div className="flex items-center gap-2 md:gap-4">
        <button
          className="lg:hidden text-gray-800"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="w-6 h-6" />
        </button>
        {activeTab === "profile" && (
          <button
            onClick={() => handleTabClick("prescriptions")}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
          {pageTitle}
        </h1>
      </div>
      {activeTab === "prescriptions" && (
        <div className="hidden md:block relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by patient name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg  border bg-white focus:outline-blue-500"
          />
        </div>
      )}
    </header>
    <div className="flex-1">
      {activeTab === "upload" ? (
        <PrescriptionUpload
          onUploadSuccess={() => {
            fetchPrescriptions();
            handleTabClick("prescriptions");
          }}
          onCancel={() => handleTabClick("prescriptions")}
        />
      ) : activeTab === "profile" ? (
        <ProfilePage />
      ) : (
        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow ring-1 ring-gray-100 overflow-hidden">
          <PrescriptionList
            prescriptions={paginatedPrescriptions}
            loading={loading}
            paginationProps={paginationProps}
          />
        </div>
      )}
    </div>
  </>
);

// --- The Main Dashboard Component (MODIFIED) ---
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("prescriptions");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        fetchPrescriptions();
      }
    }
  }, [user, navigate]);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  const fetchPrescriptions = async () => {
    setLoading(true);
    try {
      const response = await prescriptionAPI.getAll();
      setPrescriptions(response.data);
    } catch (error) {
      toast.error("Failed to fetch your prescriptions.");
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    if (tab === "prescriptions") {
      navigate("/dashboard");
    } else {
      navigate(`/dashboard/${tab}`);
    }
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    window.location.href = "/login";
  };

  const filteredPrescriptions = useMemo(() => {
    return prescriptions.filter((p) =>
      p.patient_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [prescriptions, searchTerm]);
  const paginatedPrescriptions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPrescriptions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredPrescriptions, currentPage, itemsPerPage]);
  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const paginationProps = {
    totalItems: filteredPrescriptions.length,
    itemsPerPage,
    currentPage,
    onPageChange: handlePageChange,
    onItemsPerPageChange: handleItemsPerPageChange,
  };

  if (
    !user ||
    user.role === "admin" ||
    (loading && prescriptions.length === 0)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F8FC]">
        <div className="spinner"></div>
      </div>
    );
  }
  const pageTitle = {
    prescriptions: "Dashboard",
    upload: "Upload Prescription",
    profile: "Profile",
  }[activeTab];

  // MODIFICATION: An object containing all props for DashboardView is created.
  const dashboardViewProps = {
    activeTab,
    pageTitle,
    setSidebarOpen,
    handleTabClick,
    searchTerm,
    setSearchTerm,
    fetchPrescriptions,
    paginatedPrescriptions,
    loading,
    paginationProps,
  };

  return (
    <div className="flex h-screen bg-[#F7F8FC] overflow-hidden">
      {isSidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        ></div>
      )}
      <aside
        className={`fixed lg:relative lg:drop-shadow-lg inset-y-0 left-0 w-64 bg-white flex flex-col justify-between shadow-lg lg:shadow-sm h-full z-50 transition-transform duration-300 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div>
          <div className="p-4 border-b flex items-center gap-3 lg:hidden">
            <img
              src={userAvatar}
              alt={user.first_name}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <p className="font-bold text-gray-800">
                Hello, {user.first_name}
              </p>
            </div>
          </div>
          <div className="p-6 hidden lg:block">
            <button onClick={() => handleTabClick("prescriptions")} className="focus:outline-none">
              <img
                src={logo}
                alt="Omega Medical Billing Inc."
                className="h-14 w-auto cursor-pointer"
              />
            </button>
          </div>
          <nav className="p-4 space-y-1">
            <SidebarItem
              icon={FileText}
              label="My Prescriptions"
              active={activeTab === "prescriptions"}
              onClick={() => handleTabClick("prescriptions")}
            />
            <SidebarItem
              icon={Upload}
              label="Upload Prescription"
              active={activeTab === "upload"}
              onClick={() => handleTabClick("upload")}
            />
          </nav>
        </div>
        <div className="hidden lg:block">
          <UserSidebarProfile
            user={user}
            onLogout={handleLogout}
            onNavigate={handleTabClick}
          />
        </div>
        <div className="p-4 lg:hidden">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col p-4 md:p-8 overflow-y-auto">
        <Routes>
          {/* MODIFICATION: The element for these routes is now the external DashboardView component,
              which receives all necessary data and handlers as props. */}
          <Route path="/" element={<DashboardView {...dashboardViewProps} />} />
          <Route
            path="/upload"
            element={<DashboardView {...dashboardViewProps} />}
          />
          <Route
            path="/profile"
            element={<DashboardView {...dashboardViewProps} />}
          />
          <Route path="/prescription/:id" element={<PrescriptionDetail />} />
        </Routes>
      </main>
    </div>
  );
};

// --- PrescriptionList Component (No Changes) ---
const PrescriptionList = ({ prescriptions, loading, paginationProps }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  if (loading) {
    return (
      <div className="text-center p-12">
        <div className="spinner"></div>
      </div>
    );
  }
  return (
    <>
      {prescriptions.length === 0 ? (
        <div className="text-center p-12">
          {" "}
          <FileText className="mx-auto h-12 w-12 text-gray-400" />{" "}
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No prescriptions found
          </h3>{" "}
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or upload a new prescription.
          </p>{" "}
        </div>
      ) : (
        <>
          <div className="divide-y divide-gray-200 hidden lg:block">
            {prescriptions.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between p-4 hover:bg-gray-50 gap-4"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-full mr-4">
                    <FileText className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">
                      {p.patient_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      Uploaded on {formatDate(p.created_at)}
                    </p>
                    {p.icd_codes && (
                      <p className="mt-1 text-xs text-gray-600">
                        <strong>Codes:</strong> {formatIcdCodes(p.icd_codes)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 md:gap-3">
                  <button
                    title="Download"
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <Link
                    to={`/dashboard/prescription/${p.id}`}
                    title="View Record"
                    className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                  >
                    <Eye className="w-5 h-5" />
                  </Link>
                  <button
                    title="Delete"
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="divide-y divide-gray-200 lg:hidden">
            {prescriptions.map((p) => (
              <div key={p.id} className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-gray-800">
                    {p.patient_name}
                  </p>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button title="Download">
                      <Download className="w-5 h-5 text-blue-600" />
                    </button>
                    <Link to={`/dashboard/prescription/${p.id}`} title="View">
                      <Eye className="w-5 h-5 text-green-600" />
                    </Link>
                    <button title="Delete">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium text-gray-500">Uploaded:</span>{" "}
                    {formatDate(p.created_at)}
                  </p>
                  {p.icd_codes && (
                    <p>
                      <span className="font-medium text-gray-500">Codes:</span>{" "}
                      {formatIcdCodes(p.icd_codes)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <Pagination {...paginationProps} />
    </>
  );
};

// --- Pagination Component (No Changes) ---
const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const handlePrevious = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };
  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };
  const getPageNumbers = () => {
    const pageNumbers = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      if (currentPage > 2) pageNumbers.push("...");
      if (currentPage > 1 && currentPage < totalPages)
        pageNumbers.push(currentPage);
      else if (currentPage === 1) pageNumbers.push(2);
      else if (currentPage === totalPages) pageNumbers.push(totalPages - 1);
      if (currentPage < totalPages - 1) pageNumbers.push("...");
      pageNumbers.push(totalPages);
    }
    return [...new Set(pageNumbers)];
  };
  return (
    <div className="flex flex-col md:flex-row items-center justify-between p-4 border-t gap-4">
      {" "}
      <div className="flex items-center gap-3">
        {" "}
        <select
          value={itemsPerPage}
          onChange={onItemsPerPageChange}
          className="border rounded-md text-sm p-2 bg-gray-50"
        >
          {" "}
          {[10, 20, 50].map((val) => (
            <option key={val} value={val}>
              {val.toString().padStart(2, "0")}
            </option>
          ))}{" "}
        </select>{" "}
        <span className="text-sm text-gray-600">items per page</span>{" "}
      </div>{" "}
      {totalItems > itemsPerPage && (
        <div className="flex items-center gap-1">
          {" "}
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>{" "}
          {getPageNumbers().map((page, index) =>
            typeof page === "number" ? (
              <button
                key={index}
                onClick={() => onPageChange(page)}
                className={`px-4 py-2 text-sm rounded-md ${
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ) : (
              <span key={index} className="px-2 py-2 text-sm text-gray-500">
                ...
              </span>
            )
          )}{" "}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            <ChevronRight className="w-5 h-5" />
          </button>{" "}
        </div>
      )}{" "}
    </div>
  );
};

export default Dashboard;